import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Step from "@mui/material/Step";
import Button from "@mui/material/Button";
import Stepper from "@mui/material/Stepper";
import StepLabel from "@mui/material/StepLabel";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import UploadIcon from "@mui/icons-material/Upload";
import camelcaseKeys from "camelcase-keys";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import {
  Chip,
  Container,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack
} from "@mui/material";

import conf from "../../conf";
import Spinner from "../utils/Spinner";
import InfoChip from "../utils/InfoChip";
import SectionHeader from "../utils/SectionHeader";
import ButtonWithConfirm from "../utils/ButtonWithConfirm";
import { SelectAlgorithm, SelectKeyType, SelectProject, SelectTotalComponents, SelectWrappingKey } from "./Utils";
import {
  KeySchema,
  KeyType,
  KeyWrapAlgorithm,
  OutputKeyAlgo,
  useProxyKeyComposites,
  useProxyKeyImportWrapped,
  useProxyKeyImportComponent,
  useProxyKeyImportP12,
  useProxyKeyRemove,
  useProxyKeyUploadWrapped,
  useProxyKeyUploadComponent,
  useProxyKeyUploadP12,
  useProxyKeyVerify
} from "../../hooks/proxy/keys";

const IMPORT_STEPS = ["File upload", "Confirm details", "File import"];

type ImportViewProps = {
  data?: KeySchema;
  open: boolean;
  onClose: () => void;
  onSubmit: React.Dispatch<React.SetStateAction<KeySchema | null>>;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
};

const ImportView = (props: ImportViewProps) => {
  const { data, open, onClose, onSubmit, isEditing, setIsEditing } = props;

  const [locked, setLocked] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);

  const [file, setFile] = useState<File>();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [type, setType] = useState<KeyType>("wrapped");
  const [projectId, setProjectId] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [description, setDescription] = useState("");
  const [expectedKcv, setExpectedKcv] = useState("");
  const [outputKeyName, setOutputKeyName] = useState("");
  const [outputKeyAlgo, setOutputKeyAlgo] = useState<OutputKeyAlgo>("");
  const [outputKeyAlgoDisabled, setOutputKeyAlgoDisabled] = useState<boolean>(false);

  const [uploadedKeyId, setUploadedKeyId] = useState("");
  const [importedKeyId, setImportedKeyId] = useState("");
  const [uploadedKeyDetails, setUploadedKeyDetails] = useState<KeySchema>();

  // type: wrapped
  const [wrappingAlgo, setWrappingAlgo] = useState<KeyWrapAlgorithm>("");
  const [wrappingKeyId, setWrappingKeyId] = useState("");

  // type: component
  const [compositeType, setCompositeType] = useState<"new" | "existing">("new");
  const [compositeTotal, setCompositeTotal] = useState("");
  const [compositeName, setCompositeKeyName] = useState("");

  // type: p12
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = () => setShowPassword(!showPassword);

  const composites = useProxyKeyComposites({ projectId });
  const keyRemove = useProxyKeyRemove();
  const keyVerify = useProxyKeyVerify();
  const uploadP12 = useProxyKeyUploadP12();
  const importP12 = useProxyKeyImportP12();
  const uploadWrapped = useProxyKeyUploadWrapped();
  const importWrapped = useProxyKeyImportWrapped();
  const uploadComponent = useProxyKeyUploadComponent();
  const importComponent = useProxyKeyImportComponent();

  const handleCompositeType = (event: SelectChangeEvent) => {
    setCompositeType(event.target.value as "new" | "existing");

    setExpectedKcv("");
    setOutputKeyAlgo("");
    setCompositeTotal("");
    setCompositeKeyName("");
  };

  const handleFileReset = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      setFile(undefined);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = fileInputRef.current?.files?.[0];

    if (file) {
      // console.log("selected file:", file);
      setFile(file);

      if (!outputKeyName) setOutputKeyName(file.name);
    } else {
      // console.log("no file found:", file);

      // attempt file reset
      handleFileReset();
    }
  };

  const resetForm = () => {
    setLocked(false);

    setProjectId("");
    setPartNumber("");
    setDescription("");
    setExpectedKcv("");
    setWrappingAlgo("");
    setWrappingKeyId("");
    setOutputKeyName("");
    setOutputKeyAlgo("");
    setCompositeTotal("");
    setCompositeKeyName("");

    handleFileReset();

    setUploadedKeyId("");
    setUploadedKeyDetails(undefined);

    setCompositeType("new");

    setPassword("");
  };

  const exit = () => {
    resetForm();
    setActiveStep(0);
    setOpenWarning(false);

    onClose();
    onSubmit(null);
  };

  // stepper ---------------------------------------------
  const [activeStep, setActiveStep] = React.useState(data ? 1 : 0);
  const [skipped, setSkipped] = React.useState(new Set<number>());

  const isStepSkipped = (step: number) => skipped.has(step);

  const advanceStepper = () => {
    let newSkipped = skipped;

    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleReset = () => keyRemove.mutate({ keyId: uploadedKeyId });
  const handleImportWrapped = () => importWrapped.mutate({ keyId: uploadedKeyId });
  const handleImportComponent = () => importComponent.mutate({ keyId: uploadedKeyId });
  const handleImportP12 = () =>
    importP12.mutate({
      keyId: uploadedKeyId,
      password
    });

  const handleVerify = () => keyVerify.mutate({ keyId: importedKeyId });
  const isPerformingKeyOperation =
    uploadP12.isPending ||
    importP12.isPending ||
    uploadWrapped.isPending ||
    importWrapped.isPending ||
    uploadComponent.isPending ||
    importComponent.isPending;

  const handleNext = () => {
    if (activeStep === 0) {
      if (type === "p12") {
        uploadP12.mutate({
          file,
          projectId,
          partNumber,
          outputKeyName,
          description
        });
      }

      if (type == "wrapped") {
        uploadWrapped.mutate({
          file,
          projectId,
          partNumber,
          description,
          outputKeyName,
          outputKeyAlgo,
          wrappingKeyId,
          wrappingAlgo,
          expectedKcv
        });
      }

      if (type === "component") {
        uploadComponent.mutate({
          file,
          projectId,
          partNumber,
          description,
          compositeName,
          compositeAlgo: outputKeyAlgo,
          compositeType,
          compositeTotal,
          expectedKcv
        });
      }
    } else if (activeStep === 1) {
      // confirm
      advanceStepper();
    } else if (activeStep === 2) {
      // import
      if (type === "p12") handleImportP12();
      if (type === "wrapped") handleImportWrapped();
      if (type === "component") handleImportComponent();
    }
  };

  const handleBack = () => {
    if (activeStep === 1) handleReset();
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    // note: if continuation, do not apply default key logic (not applicable)
    if (!data) {
      if (wrappingAlgo === "MLKEM") {
        setOutputKeyAlgo("AES256");
        setOutputKeyAlgoDisabled(true);
      } else {
        setOutputKeyAlgo("");
        setOutputKeyAlgoDisabled(false);
      }
    }
  }, [wrappingAlgo]);

  useEffect(() => {
    if (uploadWrapped.isSuccess) {
      const uploaded = camelcaseKeys(uploadWrapped.data.data.data) as KeySchema;

      setLocked(true);
      setUploadedKeyId(uploaded.id);
      setUploadedKeyDetails(uploaded);
      advanceStepper();

      if (data) setIsEditing(false);
    }

    if (uploadComponent.isSuccess) {
      const uploaded = camelcaseKeys(uploadComponent.data.data.data) as KeySchema;

      setLocked(true);
      setUploadedKeyId(uploaded.id);
      setUploadedKeyDetails(uploaded);
      advanceStepper();

      if (data) setIsEditing(false);
    }

    if (uploadP12.isSuccess) {
      const uploaded = camelcaseKeys(uploadP12.data.data.data) as KeySchema;

      setLocked(true);
      setUploadedKeyId(uploaded.id);
      setUploadedKeyDetails(uploaded);
      advanceStepper();

      if (data) setIsEditing(false);
    }
  }, [uploadWrapped.isSuccess, uploadComponent.isSuccess, uploadP12.isSuccess]);

  useEffect(() => {
    if (keyRemove.isSuccess) {
      setLocked(false);
      setUploadedKeyId("");
      setUploadedKeyDetails(undefined);

      if (data) setIsEditing(true);

      if (openWarning) {
        setActiveStep(0);
        setOpenWarning(false);
      }
    }
  }, [keyRemove.isSuccess]);

  useEffect(() => {
    const status = importWrapped.data?.status;

    if (importWrapped.isSuccess && status === 299) {
      const data = camelcaseKeys(importWrapped.data.data.data) as KeySchema;
      setImportedKeyId(data.id);
      setOpenWarning(true);
    }

    if (importWrapped.isSuccess && status === 200) exit();
  }, [importWrapped.isSuccess]);

  useEffect(() => {
    const status = importComponent.data?.status;

    if (importComponent.isSuccess && status === 299) {
      const data = camelcaseKeys(importComponent.data.data.data) as KeySchema;
      setImportedKeyId(data.id);
      setOpenWarning(true);
    }

    if (importComponent.isSuccess && status === 200) exit();
  }, [importComponent.isSuccess]);

  useEffect(() => {
    if (importP12.isSuccess) exit();
  }, [importP12.isSuccess]);

  useEffect(() => {
    if (keyVerify.isSuccess) exit();
  }, [keyVerify.isSuccess]);

  useEffect(() => {
    if (type === "wrapped" || type === "p12") setCompositeType("new");
  }, [type]);

  useEffect(() => {
    if (data) {
      setLocked(true);

      setType(data.type);
      setProjectId(data.project.id);
      setPartNumber(data.partNumber);
      setDescription(data.description);
      setExpectedKcv(data.expectedKcv);
      setWrappingAlgo(data.wrappingAlgo);
      setWrappingKeyId(data.wrappingKeyId);
      setUploadedKeyId(data.id);
      setUploadedKeyDetails(data);
      setOutputKeyName(data.name);
      setOutputKeyAlgo(data.algorithm);

      if (data.type === "component") {
        setPartNumber(data.compositePartNumber);
        setExpectedKcv(data.compositeExpectedKcv);
        setOutputKeyAlgo(data.compositeAlgorithm);
        setCompositeType(data.compositeId ? "existing" : "new");
        setCompositeTotal(data.compositeTotal);
        setCompositeKeyName(data.compositeName);
      }

      const fileString = atob(data.buffer);
      const fileLength = fileString.length;
      const uint8Array = new Uint8Array(fileLength);

      // populate the Uint8Array with the character codes from the binary string
      for (let i = 0; i < fileLength; i++) {
        uint8Array[i] = fileString.charCodeAt(i);
      }

      setFile(new File([uint8Array], data.originalFileName));
    }
  }, [data]);

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key="key-create-task-modal"
          title="Import"
          icon={<UploadIcon />}
          buttons={[
            <Button key={"import-reset"} size="small" color="inherit" disabled={locked || isEditing} onClick={resetForm}>
              Reset
            </Button>,
            isEditing ? (
              <ButtonWithConfirm
                key={"import-exit-with-confirm"}
                type="Exit"
                color="inherit"
                label={<CloseIcon />}
                variant="text"
                onConfirm={exit}
                message={"Are you sure you want to exit? All progress will be lost. This action cannot be undone."}
              />
            ) : (
              <Button key={"import-exit"} size="small" color="inherit" disabled={isEditing} onClick={onClose}>
                <CloseIcon />
              </Button>
            )
          ]}
        />

        <Stepper activeStep={activeStep}>
          {IMPORT_STEPS.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            const labelProps: {
              optional?: React.ReactNode;
            } = {};

            if (isStepSkipped(index)) stepProps.completed = false;

            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <Divider sx={{ my: 2 }} />

        <Box component="form" noValidate>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" alignSelf="center" spacing={1}>
                <FormLabel id="key-upload-details">Details</FormLabel>

                <InfoChip info="This tooltip would be used to explain all required fields." placement="right" />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} textAlign={"end"}>
              <SelectKeyType type={type} disabled={locked} setType={setType} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SelectProject value={projectId} required={true} disabled={locked} onChange={setProjectId} returnValue="id" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="key-part-number"
                name="key-part-number"
                label={"Part number"}
                disabled={locked}
                value={partNumber}
                onChange={(e: any) => setPartNumber(e.target.value)}
              />
            </Grid>

            {type === "p12" && (
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="key-upload-name"
                  name="key-upload-name"
                  label={"Name"}
                  disabled={locked}
                  value={outputKeyName}
                  onChange={(e: any) => setOutputKeyName(e.target.value)}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="key-upload-description"
                name="key-upload-description"
                label={"Description"}
                disabled={locked}
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
              />
            </Grid>

            {type === "wrapped" && (
              <React.Fragment>
                <Grid item xs={12} sm={6}>
                  <SelectWrappingKey
                    value={wrappingKeyId}
                    required={true}
                    disabled={locked}
                    onChange={setWrappingKeyId}
                    projectId={projectId}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SelectAlgorithm
                    label="Wrapping algorithm"
                    value={wrappingAlgo}
                    required={true}
                    disabled={locked}
                    onChange={setWrappingAlgo}
                    algorithms={conf.ALGORITHMS.WRAP}
                  />
                </Grid>
              </React.Fragment>
            )}

            {type === "component" && (
              <React.Fragment>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <InputLabel id="component-key-details">Target</InputLabel>

                    <InfoChip info="This tooltip would be used to explain all required fields." placement="right" />
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6} textAlign={"end"}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={compositeType}
                      onChange={handleCompositeType}
                      aria-labelledby="select-composite-type"
                    >
                      {["new", "existing"].map((gType) => (
                        <FormControlLabel
                          key={gType}
                          value={gType}
                          label={gType}
                          disabled={locked}
                          control={<Radio size="small" sx={{ py: 0 }} />}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {compositeType === "new" && (
                    <TextField
                      required
                      fullWidth
                      disabled={locked}
                      id="composite-key-create"
                      name="composite-key-create"
                      label="Name"
                      value={compositeName}
                      onChange={(e) => setCompositeKeyName(e.target.value)}
                      // InputProps={{ endAdornment: EndAdornment(locked) }}
                    />
                  )}

                  {compositeType === "existing" && (
                    <FormControl fullWidth>
                      <InputLabel id="composite-key-select">Name</InputLabel>

                      <Select
                        fullWidth
                        id="composite-key-select"
                        labelId="composite-key-select"
                        label="Name"
                        value={compositeName}
                        disabled={locked}
                      >
                        {composites.data?.meta.total === 0 ? (
                          <MenuItem disabled>No composite keys found</MenuItem>
                        ) : (
                          composites.data?.data.map((key) => (
                            <MenuItem
                              key={key.id}
                              value={key.name}
                              onClick={() => {
                                setPartNumber(key.partNumber);
                                setExpectedKcv(key.expectedKcv);
                                setOutputKeyAlgo(key.algorithm);
                                setCompositeTotal(key.compositeTotal);
                                setCompositeKeyName(key.name);
                              }}
                            >
                              {key.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SelectTotalComponents
                    value={compositeTotal}
                    disabled={locked || compositeType === "existing"}
                    onChange={setCompositeTotal}
                  />
                </Grid>
              </React.Fragment>
            )}

            {(type === "wrapped" || type === "component") && (
              <React.Fragment>
                {type === "wrapped" && (
                  <React.Fragment>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1}>
                        <FormLabel id="target">Target</FormLabel>

                        <InfoChip info="This tooltip would be used to explain all required fields." placement="right" />
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        disabled={locked}
                        id="wrapped-output-key-name"
                        name="wrapped-output-key-name"
                        label="Name"
                        value={outputKeyName}
                        onChange={(e) => setOutputKeyName(e.target.value)}
                      />
                    </Grid>
                  </React.Fragment>
                )}

                <Grid item xs={12} sm={6}>
                  <SelectAlgorithm
                    label="Key type"
                    value={outputKeyAlgo}
                    required={true}
                    disabled={locked || outputKeyAlgoDisabled || compositeType === "existing"}
                    onChange={setOutputKeyAlgo}
                    algorithms={conf.ALGORITHMS.OUTPUT}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="key-check-value"
                    name="key-check-value"
                    label="Key check value"
                    value={expectedKcv}
                    disabled={locked || compositeType === "existing"}
                    onChange={(e: any) => setExpectedKcv(e.target.value)}
                  />
                </Grid>
              </React.Fragment>
            )}

            {type === "p12" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  autoComplete="new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  disabled={locked}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          disabled={locked}
                          aria-label="toggle code visibility"
                          onClick={togglePassword}
                        >
                          {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={3}>
              <FormLabel id="upload-key-file">File</FormLabel>
            </Grid>

            <Grid item xs={12} sm={9}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormLabel id="uploaded-file">Uploaded</FormLabel>

                <InfoChip info="The section below provides your uploaded file details." placement="right" />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={3}>
              <input
                id="file-input"
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                disabled={locked || !!file?.name}
                style={{ display: "none" }}
              />

              <label htmlFor="file-input">
                <Button variant="contained" disabled={locked || !!file?.name || !!uploadedKeyId} component="span">
                  Select
                </Button>
              </label>

              <Grid item xs={12} sm={11} mt={2}>
                <Chip
                  variant="outlined"
                  disabled={!!uploadedKeyId}
                  label={file?.name || uploadedKeyDetails?.name || "No file selected"}
                  color={file?.name || uploadedKeyDetails?.name ? "success" : "error"}
                  onDelete={file?.name ? handleFileReset : undefined}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={9}>
              <Paper
                variant="outlined"
                elevation={uploadedKeyId ? 0 : 3}
                sx={{
                  p: 2,
                  textAlign: "left",
                  opacity: uploadedKeyId ? 1 : 0.5
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption">
                      <strong>Name:</strong> {uploadedKeyDetails?.name ?? "--"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption">
                      <strong>Size:</strong> {uploadedKeyDetails?.size ?? "--"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption">
                      <strong>
                        {uploadedKeyDetails?.type === "component" ? "Key check value (component)" : "Key check value"}:{" "}
                      </strong>
                      {uploadedKeyDetails?.computedKcv?.toUpperCase() ?? "--"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption">
                      <strong>File:</strong> {uploadedKeyDetails?.originalFileName ?? "--"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%" }}>
            {activeStep === IMPORT_STEPS.length ? (
              <React.Fragment>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Box sx={{ flex: "1 1 auto" }} />
                  <Button onClick={() => setActiveStep(0)}>Exit</Button>
                </Box>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                  <Button
                    color={activeStep === 1 ? "error" : "inherit"}
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    {keyRemove.isPending ? <Spinner sx={{ mx: 3 }} /> : activeStep === 1 ? "Undo" : "Back"}
                  </Button>

                  <Box sx={{ flex: "1 1 auto" }} />

                  {openWarning && (
                    <ButtonWithConfirm
                      fullWidth
                      type="Import"
                      label="Verify"
                      color="primary"
                      onConfirm={handleVerify}
                      onCancel={handleReset}
                      onCancelLabel="Delete"
                      onCancelLoading={keyRemove.isPending}
                      status={keyVerify.status}
                      isLoading={keyVerify.isPending}
                      showButton={false}
                      defaultOpen={openWarning}
                      message={
                        <>
                          WARNING: The expected & computed key check values <b>do not match</b>. Please delete or confirm the
                          selected key import. Please note that confirming the import will create a new pending request. This
                          action cannot be undone.
                        </>
                      }
                    />
                  )}

                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={keyRemove.isPending || isPerformingKeyOperation || (!file?.name && !uploadedKeyId)}
                  >
                    {isPerformingKeyOperation ? (
                      <Spinner sx={{ mx: 3 }} />
                    ) : activeStep === 0 ? (
                      "Upload"
                    ) : activeStep === IMPORT_STEPS.length - 1 ? (
                      "Import"
                    ) : (
                      "Continue"
                    )}
                  </Button>
                </Box>
              </React.Fragment>
            )}
          </Box>
        </Box>
      </Container>
    </Dialog>
  );
};

type ImportProps = {
  data?: KeySchema;
  disabled?: boolean;
  onSubmit: React.Dispatch<React.SetStateAction<KeySchema | null>>;
};

const Import = ({ data, disabled = false, onSubmit }: ImportProps) => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const onOpen = () => setOpen(true);
  const onClose = () => {
    if (!isEditing) setOpen(false);
  };

  return (
    <div>
      <Button
        fullWidth
        color={data ? "primary" : "secondary"}
        variant="contained"
        onClick={onOpen}
        disabled={disabled}
        startIcon={data ? null : <UploadIcon />}
      >
        {data ? "Continue" : "Import"}
      </Button>

      <ImportView
        data={data}
        open={open}
        onClose={onClose}
        onSubmit={onSubmit}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
      />
    </div>
  );
};

export default Import;
